import React, { useEffect, useRef, useState } from 'react';
import { observe, prefersReducedMotion } from './primitives';

/* ---------------------------------------------------------------------------
   Our partners — the Yunlen Business partner wall, limited to the companies
   the personal app works with (no payment rails). A row that holds still and
   changes as a whole: every mark on screen stays legible the whole time it is
   there, then the row dissolves and the next page resolves. A rolling offset
   rather than fixed pages, so every page is full whatever the count.

   Marks are third-party trademarks, used nominatively, painted one tone so the
   row reads as a credit rather than a sponsor board. Logo files are copied
   from Yunlen Business public/partners.
   --------------------------------------------------------------------------- */

const PARTNERS = [
  { name: 'Bridge', src: '/partners/bridge.png' },
  { name: 'Yellow Card', src: '/partners/yellowcard.svg' },
  { name: 'Noah', src: '/partners/noah.svg' },
  { name: 'Flutterwave', src: '/partners/flutterwave.svg' },
  { name: 'Rain', src: '/partners/rain.svg' },
];

const WIDE_PAGE = 5;
const NARROW_PAGE = 3;
const HOLD = 3000;
const FADE = 480;
const WIDE_QUERY = '(min-width: 768px)';
const LABEL = `Our partners: ${PARTNERS.map((p) => p.name).join(', ')}.`;

export default function PartnerWall() {
  const ref = useRef(null);
  const [perPage, setPerPage] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(WIDE_QUERY).matches ? WIDE_PAGE : NARROW_PAGE
  );
  const [offset, setOffset] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const perPageRef = useRef(perPage);

  useEffect(() => {
    const mq = window.matchMedia(WIDE_QUERY);
    const onChange = () => {
      const next = mq.matches ? WIDE_PAGE : NARROW_PAGE;
      perPageRef.current = next;
      setPerPage(next);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Turns only while on screen.
  useEffect(() => {
    const el = ref.current;
    // Nothing to turn when every partner already fits on one row.
    if (!el || prefersReducedMotion() || perPage >= PARTNERS.length) return undefined;
    let id = 0;
    let arrive = 0;
    const stopClock = () => {
      window.clearInterval(id);
      window.clearTimeout(arrive);
    };
    const stop = observe(
      el,
      (entry) => {
        stopClock();
        if (!entry.isIntersecting) return;
        id = window.setInterval(() => {
          setLeaving(true);
          arrive = window.setTimeout(() => {
            setOffset((o) => (o + perPageRef.current) % PARTNERS.length);
            setLeaving(false);
          }, FADE);
        }, HOLD + FADE);
      },
      { threshold: 0.15, rootMargin: '0px', once: false }
    );
    return () => {
      stopClock();
      stop();
    };
  }, [perPage]);

  const count = Math.min(perPage, PARTNERS.length);
  const page = Array.from({ length: count }, (_, i) => PARTNERS[(offset + i) % PARTNERS.length]);

  return (
    <div ref={ref} className="yl-pw" data-leaving={leaving ? '' : undefined} role="img" aria-label={LABEL}>
      {page.map((mark, i) => (
        // Keyed on the slot, which persists; the image inside is keyed on the mark.
        <div key={i} className="yl-pw-slot" style={{ '--pw-i': i }}>
          <img
            key={mark.name}
            src={mark.src}
            alt=""
            loading="lazy"
            decoding="async"
            draggable={false}
            className={`yl-pw-mark${mark.tall ? ' yl-pw-mark--tall' : ''}`}
          />
        </div>
      ))}
    </div>
  );
}
